import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/Util';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {MonomerNaturalAnalogue, MonomerVisualSymbol} from 'graphql-client/__generated/graphqlGlobalTypes';
import {widthBetweenLetters} from 'molbio/maps/linear/LinearMapUtil';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  DEGENERATE_BASE,
} from 'molbio/monomers/MonomerConstants';
import {isNaturalMonomer} from 'molbio/monomers/MonomersUtil';

import {MonomerGraphQL} from './__generated/MonomerGraphQL';
import {DEFAULT_MODIFIED_PHOSPHATE_SYMBOL, NULL_PHOSPHATE_SYMBOL} from './ModifiedPhosphate';

type Monomer = MonomerQuery['monomers'][number];

/**
 * Determine if a monomer has a visual override.
 *
 * We allow users to display normally-natural monomers as modified if they wish and have configured the
 * monomer to use a visual element in admin settings.
 */
export function naturalMonomerHasVisualOverride(monomer: Monomer | null): boolean {
  if (!monomer) {
    // We should always seed tenants with all natural monomers, but sometimes they're missing in other envs,
    // e.g. tests. We shouldn't hard block in that case, since if they don't exist, they won't have a visual
    // override.
    return false;
  }
  assert(monomer.isNatural, 'Should not call naturalMonomerHasVisualOverride on unnatural monomer');
  const defaults: {[key in keyof typeof MonomerNaturalAnalogue]: ['visualSymbol' | 'color', string]} = {
    [MonomerNaturalAnalogue.P_LOWER]: ['visualSymbol', DEFAULT_MODIFIED_PHOSPHATE_SYMBOL],
    [MonomerNaturalAnalogue.R_LOWER]: ['color', DEFAULT_MODIFIED_SUGAR_COLOR],
    [MonomerNaturalAnalogue.D_LOWER]: ['color', DEFAULT_MODIFIED_SUGAR_COLOR],
    [MonomerNaturalAnalogue.A]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.C]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.G]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.T]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.R]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.B]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.U]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.N]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.K]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.M]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.D]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.S]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.H]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.V]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.W]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
    [MonomerNaturalAnalogue.Y]: ['color', DEFAULT_MODIFIED_BASE_COLOR],
  };

  const [property, defaultVisualization] = defaults[monomer.naturalAnalogue];
  return (
    // Don't fail on mismatched hex casing
    monomer[property]?.toLowerCase() !== defaultVisualization.toLowerCase() && monomer[property] != null
  );
}

/**
 * Find the relevant natural monomer by checking whether it's natural and if its analogue matches. Note that
 * you cannot do this by checking if the symbol matches our expected symbol for the natural monomer, because
 * we let tenants override symbols (e.g. `dR` instead of the normal `d`). This assumes there is only one
 * monomer that is natural with a given analogue.
 */
function findNaturalMonomer(monomers: Array<Monomer>, monomerToFind: MonomerNaturalAnalogue): Monomer | null {
  for (const monomer of monomers) {
    if (monomer.isNatural && monomer.naturalAnalogue === monomerToFind) {
      return monomer;
    }
  }
  return null;
}

/**
 * Generate the list of modifications that should be shown in the UI for a given slice. This may include more
 * items than what the server represents as being modified, because users can configure natural monomers'
 * visualizations to look like mods as well. This function combines the list of true modification objects
 * with a list of fake objects representing the visually-overridden natural monomers.
 */
export function getDisplayModsForSlice(
  monomers: Array<Monomer>,
  nucleotideType: NucleotideType,
  sliceBases: string,
  realMods: Array<ModifiedNucleotideAttributes>,
  sliceStart: number
): Array<ModifiedNucleotideAttributes> {
  const realModsByIndex: {[index: number]: ModifiedNucleotideAttributes} = realMods.reduce(
    (acc: {[index: number]: ModifiedNucleotideAttributes}, nextMod: ModifiedNucleotideAttributes) => {
      acc[nextMod.position] = nextMod;
      return acc;
    },
    {}
  );

  const defaultSugarMonomer =
    nucleotideType === NucleotideType.DNA
      ? findNaturalMonomer(monomers, MonomerNaturalAnalogue.D_LOWER)
      : findNaturalMonomer(monomers, MonomerNaturalAnalogue.R_LOWER);
  const hasNonDefaultSugarVisual = naturalMonomerHasVisualOverride(defaultSugarMonomer);
  const defaultPhosphateMonomer = findNaturalMonomer(monomers, MonomerNaturalAnalogue.P_LOWER);
  const hasNonDefaultPhosphateVisual = naturalMonomerHasVisualOverride(defaultPhosphateMonomer);

  const mods: Array<ModifiedNucleotideAttributes> = [];
  sliceBases.split('').forEach((base: string, index: number) => {
    const realIndex = index + sliceStart;
    const existingMod = realModsByIndex[realIndex];
    if (existingMod) {
      mods.push(existingMod);
    } else {
      const defaultBaseMonomer = findNaturalMonomer(monomers, base.toUpperCase() as MonomerNaturalAnalogue);
      const hasNonDefaultBaseVisual = naturalMonomerHasVisualOverride(defaultBaseMonomer);
      // Make a fake mod if the nucleotide has any non-default visualizations
      if (hasNonDefaultBaseVisual || hasNonDefaultPhosphateVisual || hasNonDefaultSugarVisual) {
        // Error here if any relevant default monomers are missing. They shouldn't be - all tenants are
        // initialized with all natural monomers. However, if we did run into this, we'd only want to throw
        // an error when we actually would have needed to use the missing monomer.
        if (!defaultBaseMonomer || !defaultSugarMonomer || !defaultPhosphateMonomer) {
          assert(
            false,
            `Missing default monomers. Sugar: ${defaultSugarMonomer}, base: ${defaultBaseMonomer}, phosphate: ${defaultPhosphateMonomer}`
          );
        }
        mods.push({
          position: realIndex,
          sugarMonomerId: defaultSugarMonomer.id,
          baseMonomerId: defaultBaseMonomer.id,
          phosphateMonomerId: defaultPhosphateMonomer.id,
        });
      }
    }
  });

  return mods;
}

export function displayAsNatural(
  monId: string | null,
  monomers: Array<Monomer>,
  nucleotideType: NucleotideType,
  isOligo: boolean
): boolean {
  if (!monId) {
    return false;
  }
  if (monId === DEGENERATE_BASE) {
    return true;
  }
  const monomer = monomers.filter((m) => m.id === monId)[0];
  assert(monomer != null, `Expected monomer with ${monId} to be defined`);
  if (isNaturalMonomer(monomer, nucleotideType) && naturalMonomerHasVisualOverride(monomer)) {
    return false;
  }
  if (isOligo && BenchlingConfig.MOLBIO_ENABLE_UT_FLEXIBILITY) {
    const flexibleBase =
      nucleotideType === NucleotideType.DNA ? MonomerNaturalAnalogue.U : MonomerNaturalAnalogue.T;
    if (monomer.isNatural && monomer.naturalAnalogue === flexibleBase) {
      return true;
    }
  }
  return isNaturalMonomer(monomer, nucleotideType);
}

export function letterCenterX(xFn: (index: number) => number, sliceStart: number, i: number): number {
  return letterStartX(xFn, sliceStart, i) + 0.5 * widthBetweenLetters(xFn, sliceStart);
}

export function letterStartX(xFn: (index: number) => number, sliceStart: number, i: number): number {
  return (i - sliceStart) * widthBetweenLetters(xFn, sliceStart);
}

export function getVisualSymbolForPhosphateMonomer(
  phosphateMonomer?: Monomer | MonomerGraphQL
): MonomerVisualSymbol {
  if (phosphateMonomer == null) {
    // null phosphate
    return NULL_PHOSPHATE_SYMBOL;
  } else if (phosphateMonomer.visualSymbol != null) {
    // modified phosphate with specified visual symbol
    return phosphateMonomer.visualSymbol;
  } else {
    // modified phosphate with unspecified visual symbol
    return DEFAULT_MODIFIED_PHOSPHATE_SYMBOL;
  }
}
